import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_CROSS_TABS,
  readSurveyResponsesSummaryGPT,
  SURVEY_TYPES,
} from '@api/instant_survey';

import {
  formatLaunchPayment,
  shareInstantSurveyWithClientGroup,
  shareInstantSurveyWithUsers,
  surveyStatus,
} from '@api/surveys';

import {
  exportMaxDiffPrefShareData,
  exportRawData,
  exportRawDataSpss,
  exportRawDataV2,
  exportStatXtabData,
  exportSummaryData,
  readGlobalFilters,
  readGlobalFiltersV2,
  readRawData,
  readRawDataV2,
  readSurveyDashboardRequests,
  readTurfSimulatorData,
  readTurfSimulatorDataV2,
} from '@api/survey_dashboard';
import { GATES } from '@constants';

import { exportPowerPoint } from './utilities/helpers';
import {
  applyQuestionTranslations,
  getAvailableLanguages,
  getSourceLanguage,
  getValidAppliedLanguage,
} from './utilities/languageTranslation';
import { downloadDOCXFile } from '../../../../components/DownloadDOCXButton/utilities/helpers';

import {
  Announcement,
  Button,
  ButtonText,
  ColumnsIcon,
  DownloadIcon,
  getFormattedDate,
  IconButton,
  isCalypsoSurvey,
  Layout,
  LoaderSkeleton,
  MinusIcon,
  OverlayPopover,
  PlusIcon,
  Radio,
  SaveSmartPeopleGroupIcon,
  ShareIcon,
  Tabs,
  toast,
  Toolbar,
  TranslateIcon,
} from '@utilities';

import CrosstabBuilder from './components/CrosstabBuilder';
import { createOverallSpan } from './components/CrosstabBuilder/utilities/helpers';
import Details from './components/Details';
import InsightsDetails from './components/InsightsDetails';
import ModalShare from '../../../../components/ModalShare';
import ModalSurveyPeopleGroup from '../../../../components/ModalSurveyPeopleGroup';
import RawData from './components/RawData';
import ResponseQuality from './components/ResponseQuality';
import Responses from './components/Responses';
import TurfSimulator from './components/TurfSimulator';

import { SEARCH_PARAM_KEYS } from '../../utilities/constants';
import { useTheme } from '@utilities';
import { DISPLAY_AS } from './components/Responses/utilities/helpers';
import { getSurveyQuestionCount } from './utilities/questionCount';
import {
  canShowCrosstabBuilderTab,
  canShowResponseQualityTab,
  canShowTurfSimulatorTab,
  CROSSTAB_BUILDER_TAB,
  TURF_SIMULATOR_TAB,
} from './utilities/tabs';
import useGlobalFiltersUrlState from './utilities/useGlobalFiltersUrlState';

import styles from './_index.module.scss';

const BASE_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'responses', label: 'Results' },
  { id: 'raw-data', label: 'Raw Data' },
];

const DEFAULT_SURVEY_DASHBOARD_TAB_ID = BASE_TABS[0].id;
const RESPONSE_QUALITY_TAB = { id: 'response-quality', label: 'Response Quality' };

const getDisplayAsFromParam = (displayAsParam) => {
  return displayAsParam === DISPLAY_AS.COUNT.value ? DISPLAY_AS.COUNT : DISPLAY_AS.PERCENT;
};

// Per-question settings (sort-by, sort-order, significance) share one shape: a
// `{ [questionId]: value }` map held in a ref (read synchronously by the export
// memo) and, when the setting drives dashboard-level rendering, mirrored into
// React state via `setState`. Settings whose rendering state lives inside the
// question component (significance) omit `setState` so updating them doesn't
// rerender every question. `isCleared` means the value is the setting's
// default/off state, so the question's entry is removed rather than stored.
const updateQuestionSettingMap = ({ isCleared, questionId, ref, setState, value }) => {
  const prev = ref.current;
  let nextState = prev;

  if (isCleared) {
    if (questionId in prev) {
      nextState = { ...prev };
      delete nextState[questionId];
    }
  } else if (prev[questionId] !== value) {
    nextState = { ...prev, [questionId]: value };
  }

  if (nextState !== prev) {
    ref.current = nextState;
    setState?.(nextState);
  }
};

const Dashboard = ({
  details,
  insightsDocument,
  insightsType,
  publicToken,
  survey,
  surveyQuestions,
  surveyType,
}) => {
  const theme = useTheme();

  let { survey_id: surveyId, tab_id: tabId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const displayAs = getDisplayAsFromParam(searchParams.get(SEARCH_PARAM_KEYS.DISPLAY_AS));
  const [completionRateData, setCompletionRateData] = useState(null);
  const [crosstabBanner, setCrosstabBanner] = useState(() => [createOverallSpan()]);
  const [crosstabQuestionConfigs, setCrosstabQuestionConfigs] = useState([]);
  const [keyFindingsMap, setKeyFindingsMap] = useState({});
  const [globalFilters, setGlobalFilters] = useState();
  const [globalFiltersError, setGlobalFiltersError] = useState();
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = useState(false);
  const sourceLanguage = getSourceLanguage(survey?.languageInfo);
  const availableLanguages = useMemo(() => getAvailableLanguages(survey?.languageInfo), [survey]);
  const [appliedLanguage, setAppliedLanguage] = useState(sourceLanguage);
  const [pendingLanguage, setPendingLanguage] = useState(sourceLanguage);

  useEffect(() => {
    setAppliedLanguage((currentLanguage) =>
      getValidAppliedLanguage({
        availableLanguages,
        currentLanguage,
        sourceLanguage,
      })
    );
    setPendingLanguage((currentLanguage) =>
      getValidAppliedLanguage({
        availableLanguages,
        currentLanguage,
        sourceLanguage,
      })
    );
  }, [availableLanguages, sourceLanguage]);

  const [isRawDataLoading, setIsRawDataLoading] = useState(true);
  const [isResponseDataLoading, setIsResponseDataLoading] = useState(true);
  const [isTurfSimulatorDataLoading, setIsTurfSimulatorDataLoading] = useState(true);
  const [rawData, setRawData] = useState();
  const [rawDataLoadingError, setRawDataLoadingError] = useState();
  const [responseData, setResponseData] = useState();
  const [videoHighlights, setVideoHighlights] = useState(null);
  const [responseLoadingError, setResponseLoadingError] = useState();
  const [sortByGroupByQuestion, setSortByGroupByQuestion] = useState({});
  const [sortOrderByQuestion, setSortOrderByQuestion] = useState({});
  const [showModalShare, setShowModalShare] = useState(false);
  const [showModalSurveyPeopleGroup, setShowModalSurveyPeopleGroup] = useState(false);
  const [showResearchObjective, setShowResearchObjective] = useState(false);
  const [includeDisqualifiedResponses, setIncludeDisqualifiedResponses] = useState(false);
  const [responseCounts, setResponseCounts] = useState(null);
  const [turfSimulatorData, setTurfSimulatorData] = useState();
  const [turfSimulatorDataLoadingError, setTurfSimulatorDataLoadingError] = useState();

  const downloadButtonRef = useRef();
  const translateButtonRef = useRef();
  const filtersRequestController = useRef();
  const isVideoSurvey = surveyType?.id === SURVEY_TYPES.VIDEO;
  const navigate = useNavigate();
  const rawDataRequestController = useRef();
  const responseRequestController = useRef();
  const sortByGroupByQuestionRef = useRef({});
  const sortOrderByQuestionRef = useRef({});
  const significanceByQuestionRef = useRef({});
  const turfSimulatorDataRequestController = useRef();

  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const user = useSelector((state) => state?.user?.data);
  const isResponseQualityGateEnabled =
    !isStatsigLoading && client.checkGate(GATES.RESPONSE_QUALITY_TAB);
  const canShowResponseQuality = canShowResponseQualityTab({
    isResponseQualityGateEnabled,
    isStatsigLoading,
    publicToken,
  });
  const useV2SurveyResponses = client.checkGate(GATES.SURVEY_DASHBOARD_V2_RESPONSES);
  // The redesign's filters only take effect on v2 responses; requiring both gates avoids
  // a picker that appears to filter but silently doesn't.
  const showGlobalFiltersRedesign =
    !isStatsigLoading &&
    client.checkGate(GATES.GLOBAL_FILTERS_REDESIGN) &&
    useV2SurveyResponses &&
    !isVideoSurvey;
  const {
    globalFilterFacets,
    globalFilterSelection,
    globalFiltersRedesignError,
    globalFiltersRedesignState,
    isGlobalFiltersRedesignLoading,
    isGlobalFiltersRedesignReady,
    onGlobalFilterSelectionChange,
  } = useGlobalFiltersUrlState({
    enabled: showGlobalFiltersRedesign,
    publicToken,
    surveyId,
    surveyQuestions,
  });
  const isTurfSimulatorGateEnabled =
    !publicToken && !isStatsigLoading && client.checkGate(GATES.TURF_SIMULATOR);
  const canUseSurveyTranslation = !isStatsigLoading && client.checkGate(GATES.SURVEY_TRANSLATION);
  const canShowFullQuestionnaire =
    !isStatsigLoading && client.checkGate(GATES.DISPLAY_FULL_QNR_IN_REPORT);
  const launchPayment = formatLaunchPayment(
    insightsDocument.paymentAmount,
    insightsDocument.paymentType
  );
  const shouldShowLaunchPayment = Boolean(
    !isStatsigLoading &&
    client.checkGate(GATES.SHOW_CREDITS) &&
    insightsDocument.shouldDisplayLaunchPayment &&
    launchPayment
  );
  const canExportReportQuestionnaire = canShowFullQuestionnaire && isCalypsoSurvey(survey);
  const canShowTurfSimulator = canShowTurfSimulatorTab({
    isStatsigLoading,
    isTurfSimulatorGateEnabled,
    publicToken,
    surveyQuestions,
  });
  const isCrosstabBuilderGateEnabled =
    !isStatsigLoading && client.checkGate(GATES.CUSTOM_CROSSTAB_BUILDER);
  const canShowCrosstabBuilder = canShowCrosstabBuilderTab({
    isCrosstabBuilderGateEnabled,
    isStatsigLoading,
    publicToken,
    surveyQuestions,
  });
  const SURVEY_DASHBOARD_TABS = useMemo(() => {
    const tabs = [...BASE_TABS];

    if (canShowCrosstabBuilder) tabs.push(CROSSTAB_BUILDER_TAB);
    if (canShowTurfSimulator) tabs.push(TURF_SIMULATOR_TAB);
    if (canShowResponseQuality) tabs.push(RESPONSE_QUALITY_TAB);

    return tabs;
  }, [canShowCrosstabBuilder, canShowResponseQuality, canShowTurfSimulator]);
  const canCrossTabs = useMemo(() => {
    return (
      !isVideoSurvey &&
      surveyQuestions?.some(({ type }) => QUESTION_TYPES_WITH_CROSS_TABS.includes(type))
    );
  }, [isVideoSurvey, surveyQuestions]);
  const canPreferenceShare = useMemo(() => {
    return surveyQuestions?.some(
      ({ type }) =>
        type === QUESTION_TYPES.MAX_DIFF.externalType || type === QUESTION_TYPES.MAX_DIFF.value
    );
  }, [surveyQuestions]);
  const isDisqualifiedResponseDataEnabled = client.checkGate(
    GATES.SURVEY_INCLUDE_DISQUALIFIED_RESPONSES
  );
  const statusFilters = {
    canIncludeDisqualifiedResponses: isDisqualifiedResponseDataEnabled && isCalypsoSurvey(survey),
  };

  const compareByOptions = useMemo(
    () => [
      {
        label: 'Primary Segments',
        options: [
          { label: 'Overall', value: 'overall' },
          ...(globalFilters?.quotaGroups?.length > 1
            ? [{ label: 'Quota Group', value: 'quota_group' }]
            : []),
        ],
      },
      {
        label: 'Demographic Segments',
        options: globalFilters?.demographicFilters
          ?.filter(({ compareBy }) => compareBy)
          .map(({ id, label }) => ({
            label,
            value: id,
          })),
      },
    ],
    [globalFilters, survey]
  );
  const questionsRef = survey ? survey.questions : insightsDocument.questions;
  const questionsCount = getSurveyQuestionCount(questionsRef);
  const selectedCompareBy = useMemo(() => {
    if (!showGlobalFiltersRedesign && !globalFilters) return null;

    // Force 'overall' for video surveys to prevent hidden compare-by from affecting data
    if (isVideoSurvey) {
      return compareByOptions[0].options[0]; // 'overall'
    }

    if (showGlobalFiltersRedesign) {
      return globalFiltersRedesignState.selectedCompareBy || compareByOptions[0].options[0];
    }

    const compareByValue = searchParams.get(SEARCH_PARAM_KEYS.COMPARE_BY);
    if (!compareByValue)
      return globalFilters?.quotaGroups?.length > 1
        ? compareByOptions[0].options[1]
        : compareByOptions[0].options[0];

    return [...compareByOptions[0].options, ...compareByOptions[1].options].find(
      ({ value }) => value === compareByValue
    );
  }, [
    globalFilters,
    searchParams,
    isVideoSurvey,
    compareByOptions,
    showGlobalFiltersRedesign,
    globalFiltersRedesignState.selectedCompareBy,
  ]);
  const selectedDemographicFilters = useMemo(() => {
    const searchParamsObject = Object.fromEntries(searchParams);

    return (
      Object.keys(searchParamsObject)
        // only include properties that are in the demographic filters
        .filter((key) => globalFilters?.demographicFilters.find(({ id }) => id === key))
        .reduce((acc, key) => {
          acc[key] = searchParamsObject[key]?.split(',');
          return acc;
        }, {})
    );
  }, [globalFilters, searchParams]);
  const selectedOptionIds = searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION)?.split(',') || [];
  const selectedQuotaGroups = useMemo(() => {
    return searchParams
      .get(SEARCH_PARAM_KEYS.QUOTA_GROUP)
      ?.split(',')
      .map((groupRef) =>
        globalFilters?.quotaGroups.find(
          ({ instantSurveyGroupRef }) => instantSurveyGroupRef === groupRef
        )
      )
      .filter(Boolean);
  }, [globalFilters, searchParams]);

  useEffect(() => {
    // Clear compare-by query param for video surveys to prevent hidden filter state
    if (isVideoSurvey && searchParams.has(SEARCH_PARAM_KEYS.COMPARE_BY)) {
      const params = new URLSearchParams(searchParams);
      params.delete(SEARCH_PARAM_KEYS.COMPARE_BY);
      setSearchParams(params, { replace: true });
    }
  }, [isVideoSurvey, searchParams, setSearchParams]);

  useEffect(() => {
    setSortByGroupByQuestion({});
    setSortOrderByQuestion({});
    sortByGroupByQuestionRef.current = {};
    sortOrderByQuestionRef.current = {};
  }, [selectedCompareBy?.value]);

  const exportQuery = useMemo(() => {
    if (showGlobalFiltersRedesign) {
      return {
        includeDisqualified: includeDisqualifiedResponses,
        ...globalFiltersRedesignState.query,
      };
    }

    return {
      compare_by:
        selectedCompareBy?.value !== compareByOptions[0].options[0].value
          ? selectedCompareBy?.value
          : '',
      includeDisqualified: includeDisqualifiedResponses,
      language: appliedLanguage && appliedLanguage !== sourceLanguage ? appliedLanguage : '',
      option_filter: selectedOptionIds.join(';'),
      quota_group_filter: selectedQuotaGroups
        ?.flatMap(({ quotaGroupRefs }) => quotaGroupRefs)
        .join(';'),
      ...Object.fromEntries(
        Object.entries(selectedDemographicFilters).map(([key, value]) => [key, value.join(';')])
      ),
    };
  }, [
    appliedLanguage,
    compareByOptions,
    globalFiltersRedesignState.query,
    includeDisqualifiedResponses,
    selectedCompareBy,
    selectedDemographicFilters,
    selectedOptionIds,
    selectedQuotaGroups,
    showGlobalFiltersRedesign,
    sourceLanguage,
  ]);
  // exportQuery's reference changes every render, so key the fetch effect off its content instead.
  const exportQueryKey = useMemo(() => JSON.stringify(exportQuery), [exportQuery]);
  const isReportingInitializationReady =
    !isStatsigLoading &&
    (showGlobalFiltersRedesign ? isGlobalFiltersRedesignReady : Boolean(globalFilters));

  useEffect(() => {
    // redirect to first tab if invalid tab id
    if (isStatsigLoading) return;

    if (tabId && SURVEY_DASHBOARD_TABS.every(({ id }) => id !== tabId)) {
      const firstTabId = SURVEY_DASHBOARD_TABS[0].id;
      navigate(
        publicToken
          ? `/share/survey/${publicToken}/${insightsDocument.id}/${firstTabId}`
          : `/dashboard/survey/${insightsDocument.id}/${firstTabId}`,
        { replace: true }
      );
    }
  }, [SURVEY_DASHBOARD_TABS, insightsDocument.id, isStatsigLoading, navigate, publicToken, tabId]);

  useEffect(() => {
    // Fetch GPT summaries and key findings when survey and compare-by selection change
    if (!survey?.id || !selectedCompareBy) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchGPTData = async () => {
      try {
        const gptData = await readSurveyResponsesSummaryGPT({
          signal,
          survey_id: survey.id,
        });

        // Set GPT data for key findings and response summaries
        setKeyFindingsMap(gptData);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('[Dashboard] Failed to fetch GPT data:', error);
          setKeyFindingsMap({});
        }
      }
    };

    fetchGPTData();
    return () => controller.abort();
  }, [survey, selectedCompareBy]);

  useEffect(() => {
    getFilters();
    turfSimulatorDataRequestController?.current?.abort();
    setTurfSimulatorData();
    setTurfSimulatorDataLoadingError();
    setIsTurfSimulatorDataLoading(true);

    return () => {
      filtersRequestController?.current?.abort();
      turfSimulatorDataRequestController?.current?.abort();
    };
  }, [insightsDocument]);

  useEffect(() => {
    if (
      canShowTurfSimulator &&
      tabId === TURF_SIMULATOR_TAB.id &&
      !turfSimulatorData &&
      !turfSimulatorDataLoadingError &&
      !turfSimulatorDataRequestController.current
    ) {
      getTurfSimulatorData();
    }
  }, [
    canShowTurfSimulator,
    insightsDocument,
    tabId,
    turfSimulatorData,
    turfSimulatorDataLoadingError,
  ]);

  useEffect(() => {
    getRawData();

    return () => rawDataRequestController?.current?.abort();
  }, [
    insightsDocument,
    includeDisqualifiedResponses,
    statusFilters.canIncludeDisqualifiedResponses,
  ]);

  const responseLocationKey = useMemo(() => {
    if (showGlobalFiltersRedesign) return searchParams.get('_r') || '';

    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_KEYS.DISPLAY_AS);
    params.delete(SEARCH_PARAM_KEYS.TURF_BASKET_STATE);
    params.delete(SEARCH_PARAM_KEYS.TURF_INCLUDE_SPECIAL);
    params.delete(SEARCH_PARAM_KEYS.TURF_ITEMS);
    params.delete(SEARCH_PARAM_KEYS.TURF_MAX_SIZE);
    params.delete(SEARCH_PARAM_KEYS.TURF_PINS);
    params.delete(SEARCH_PARAM_KEYS.TURF_QUESTION);
    return params.toString();
  }, [searchParams, showGlobalFiltersRedesign]);

  useEffect(() => {
    if (isReportingInitializationReady) {
      getResponseData();
    }

    return () => responseRequestController?.current?.abort();
  }, [
    exportQueryKey,
    includeDisqualifiedResponses,
    isReportingInitializationReady,
    responseLocationKey,
    statusFilters.canIncludeDisqualifiedResponses,
  ]);

  const getFilters = async () => {
    const controller = new AbortController();
    filtersRequestController.current = controller;

    try {
      if (isVideoSurvey) {
        setGlobalFilters(
          await readGlobalFiltersV2({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      } else {
        setGlobalFilters(
          await readGlobalFilters({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setGlobalFiltersError(error);
      }
    }

    setIsFiltersLoading(false);
  };

  const getRawData = async () => {
    rawDataRequestController.current?.abort();
    const controller = new AbortController();
    rawDataRequestController.current = controller;
    setIsRawDataLoading(true);
    setRawDataLoadingError(null);

    // Ignore results from a request that has been superseded (e.g. the disqualified toggle
    // changed) or aborted, so a stale response can't overwrite the active request's state.
    const isStale = () =>
      controller.signal.aborted || rawDataRequestController.current !== controller;

    try {
      const params = {
        includeDisqualified:
          statusFilters.canIncludeDisqualifiedResponses && includeDisqualifiedResponses,
        publicToken,
        signal: controller.signal,
        surveyId,
      };
      const response = isVideoSurvey ? await readRawDataV2(params) : await readRawData(params);
      if (isStale()) return;
      setRawData(response);
      setIsRawDataLoading(false);
    } catch (error) {
      if (isStale()) return;
      setRawDataLoadingError(error);
      setIsRawDataLoading(false);
    }
  };

  const getTurfSimulatorData = async () => {
    turfSimulatorDataRequestController?.current?.abort();
    const controller = new AbortController();
    turfSimulatorDataRequestController.current = controller;
    setIsTurfSimulatorDataLoading(true);
    setTurfSimulatorData();
    setTurfSimulatorDataLoadingError();

    try {
      const params = { signal: controller.signal, surveyId };
      const response = isVideoSurvey
        ? await readTurfSimulatorDataV2(params)
        : await readTurfSimulatorData(params);
      setTurfSimulatorData(response);
      setIsTurfSimulatorDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        const status = error?.response?.status;

        if (Number(status) >= 500) {
          console.warn('[Dashboard] Failed to fetch TURF simulator data:', {
            endpointVariant: isVideoSurvey ? 'survey-api' : 'snowflake',
            error,
            status,
            surveyId,
          });
        }

        setTurfSimulatorDataLoadingError(error);
        setIsTurfSimulatorDataLoading(false);
      }
    } finally {
      if (turfSimulatorDataRequestController.current === controller) {
        turfSimulatorDataRequestController.current = undefined;
      }
    }
  };

  const getResponseData = async () => {
    responseRequestController?.current?.abort();
    const controller = new AbortController();
    responseRequestController.current = controller;

    setIsResponseDataLoading(true);
    setCompletionRateData(null);
    setResponseCounts(null);
    setResponseData(null);
    setVideoHighlights(null);
    setResponseLoadingError(null);

    const params = {
      publicToken,
      query: exportQuery,
      signal: controller.signal,
      surveyId,
      useV2Responses: useV2SurveyResponses,
    };
    try {
      const { anyResponseFulfilled, completionRate, responseCounts, responseData } =
        await readSurveyDashboardRequests({
          params,
          shouldFetchCompletionRate: statusFilters.canIncludeDisqualifiedResponses,
          surveyQuestions,
        });

      if (controller.signal.aborted) return;

      if (responseCounts.status === 'fulfilled') {
        setResponseCounts(responseCounts.value);
      } else {
        console.warn(
          '[Dashboard] Failed to fetch readSurveyResponseCounts:',
          responseCounts.reason
        );
      }
      if (completionRate?.status === 'fulfilled') {
        setCompletionRateData(completionRate.value);
      } else if (completionRate?.status === 'rejected') {
        console.warn('[Dashboard] Failed to fetch readCompletionRate:', completionRate.reason);
      }
      if (!anyResponseFulfilled) {
        setResponseLoadingError(new Error('Failed to load survey response data.'));
        setIsResponseDataLoading(false);
        return;
      }
      // The survey-level video highlights ride the same payload as the questions, but
      // they are not a question. Pull them out before building the question map so
      // Object.values(responseData) (PowerPoint export, surveyResponsesArray) keeps
      // containing only real questions.
      setVideoHighlights(
        responseData.find((response) => response?.type === 'VIDEO_HIGHLIGHTS') ?? null
      );
      setResponseData(
        responseData.reduce((acc, response) => {
          if (response?.type === 'VIDEO_HIGHLIGHTS') return acc;
          if (response?.id != null) acc[response.id] = response;
          return acc;
        }, {})
      );

      setIsResponseDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setResponseLoadingError(error);
        setIsResponseDataLoading(false);
      }
    }
  };
  const handleExportPowerPoint = async () => {
    const surveyResponsesArray = responseData ? Object.values(responseData).filter(Boolean) : [];
    if (!surveyResponsesArray.length) {
      toast.error(
        'No survey response data available for PowerPoint export. Please ensure the survey responses have loaded.'
      );
      return;
    }

    toast('Generating PowerPoint, please wait...');
    try {
      await exportPowerPoint({
        areFiltersApplied: showGlobalFiltersRedesign
          ? Boolean(globalFiltersRedesignState.query.row_operator)
          : undefined,
        details,
        displayAs,
        globalFilters,
        insightsDocument,
        keyFindingsMap,
        questionsRef,
        responseData,
        searchParams,
        selectedCompareBy,
        sortByGroupByQuestion: sortByGroupByQuestionRef.current,
        sortOrderByQuestion: sortOrderByQuestionRef.current,
        survey,
        surveyQuestions,
        theme,
      });
      toast.success('PowerPoint file has been downloaded');
    } catch (error) {
      console.error('PowerPoint generation error:', error);
      toast.error('Failed to generate PowerPoint presentation: ' + error.message);
    }
  };

  const handleExportDocx = async () => {
    if (!survey) {
      toast.error('No survey data available for Word document export.');
      return;
    }

    try {
      await downloadDOCXFile({
        includeTranslations: canUseSurveyTranslation,
        survey,
        userEmail: user?.email,
      });
    } catch (error) {
      console.error('DOCX generation error:', error);
      toast.error(`Failed to generate Word document${error?.message ? `: ${error.message}` : ''}`);
    }
  };

  const handleExportDownload = async (exportDownloadAPI) => {
    try {
      await exportDownloadAPI({
        includeDisqualified:
          statusFilters.canIncludeDisqualifiedResponses && includeDisqualifiedResponses,
        query: exportQuery,
        surveyId,
      });
    } catch (error) {
      console.error('Error occurred while exporting data', error);
      toast.error('Error occurred while exporting data.');
    }
  };

  const handleUpdateIncludeDisqualifiedResponses = () =>
    setIncludeDisqualifiedResponses((prev) => !prev);

  const areExportOpsDisabled = isRawDataLoading || isResponseDataLoading;
  const downloadTooltipText = areExportOpsDisabled
    ? 'Download will be available after report has finished processing'
    : 'Download data';
  const surveyPeopleGroupTooltipText = areExportOpsDisabled
    ? 'Save Survey People Group will be available after report has finished processing'
    : 'Save Survey People Group';

  const surveyTitle = survey?.title || insightsDocument.title;

  const handleQuestionSortByChange = (questionId, value) =>
    updateQuestionSettingMap({
      isCleared: !value || value === 'default',
      questionId,
      ref: sortByGroupByQuestionRef,
      setState: setSortByGroupByQuestion,
      value,
    });

  const handleQuestionSortOrderChange = (questionId, value) =>
    updateQuestionSettingMap({
      isCleared: !value || value === 'default',
      questionId,
      ref: sortOrderByQuestionRef,
      setState: setSortOrderByQuestion,
      value,
    });

  /**
   * Handles per-question significance on/off and confidence. `alpha` null disables
   * significance (and removes the key); a number enables it at that confidence.
   * Deliberately no `setState`: the alpha that drives rendering lives inside the
   * affected question's ComparisonCharts, so a confidence change doesn't rerender
   * the whole report. This ref persists the choice across question remounts (data
   * refetches, tab switches); `getQuestionSignificance` reads it back to reseed that state.
   */
  const handleQuestionSignificanceChange = (questionId, alpha) =>
    updateQuestionSettingMap({
      isCleared: alpha == null,
      questionId,
      ref: significanceByQuestionRef,
      value: alpha,
    });

  const getQuestionSignificance = (questionId) => significanceByQuestionRef.current[questionId];

  const handleDisplayAsChange = (newDisplayAs) => {
    searchParams.set(SEARCH_PARAM_KEYS.DISPLAY_AS, newDisplayAs.value);
    setSearchParams(searchParams);
  };

  const hasTranslations = canUseSurveyTranslation && availableLanguages.length > 0;

  // Questions with their text / option labels swapped to the applied language. The source language
  // shows the content as authored (no translation applied). Fed to the Results tab so switching
  // language re-renders translated content.
  const translatedSurveyQuestions = useMemo(
    () =>
      appliedLanguage && appliedLanguage !== sourceLanguage
        ? applyQuestionTranslations(surveyQuestions, appliedLanguage)
        : surveyQuestions,
    [appliedLanguage, sourceLanguage, surveyQuestions]
  );
  const translatedQuestionnaireSurvey = useMemo(
    () =>
      survey && appliedLanguage && appliedLanguage !== sourceLanguage
        ? {
            ...survey,
            questions: applyQuestionTranslations(survey.questions, appliedLanguage),
          }
        : survey,
    [appliedLanguage, sourceLanguage, survey]
  );

  const handleOpenLanguagePopover = () => {
    setPendingLanguage(appliedLanguage);
    setIsLanguagePopoverOpen(true);
  };

  const handleApplyLanguage = () => {
    setAppliedLanguage(pendingLanguage);
    setIsLanguagePopoverOpen(false);
  };

  return (
    <>
      <div>
        <Toolbar
          tools={
            publicToken
              ? []
              : [
                  hasTranslations ? (
                    <IconButton
                      key="translate-button"
                      onClick={handleOpenLanguagePopover}
                      ref={translateButtonRef}
                      tooltip="Translate"
                    >
                      <TranslateIcon />
                    </IconButton>
                  ) : null,
                  <IconButton
                    isDisabled={areExportOpsDisabled}
                    key="download-button"
                    onClick={() => setIsPopoverOpen(true)}
                    ref={downloadButtonRef}
                    tooltip={downloadTooltipText}
                  >
                    <DownloadIcon />
                  </IconButton>,
                  <>
                    {!isVideoSurvey ? (
                      <IconButton
                        isDisabled={areExportOpsDisabled}
                        key="save-survey-people-group"
                        onClick={() => setShowModalSurveyPeopleGroup(true)}
                        tooltip={surveyPeopleGroupTooltipText}
                      >
                        <SaveSmartPeopleGroupIcon />
                      </IconButton>
                    ) : null}
                  </>,
                  <IconButton key="share" onClick={() => setShowModalShare(true)} tooltip={'Share'}>
                    <ShareIcon />
                  </IconButton>,
                  <OverlayPopover
                    isActive={isPopoverOpen}
                    key="download-popover"
                    onHide={() => setIsPopoverOpen(false)}
                    target={downloadButtonRef?.current}
                  >
                    <ul>
                      {!isVideoSurvey && (
                        <li>
                          <button onClick={() => handleExportDownload(exportSummaryData)}>
                            <span>Summary Tables</span>
                          </button>
                        </li>
                      )}
                      <li>
                        <button
                          onClick={() =>
                            handleExportDownload(isVideoSurvey ? exportRawDataV2 : exportRawData)
                          }
                        >
                          <span>Raw Data</span>
                        </button>
                      </li>
                      {!isVideoSurvey && (
                        <li>
                          <button onClick={() => handleExportDownload(exportRawDataSpss)}>
                            <span>SPSS</span>
                          </button>
                        </li>
                      )}
                      {canCrossTabs && (
                        <li>
                          <button
                            onClick={() => {
                              if (canShowCrosstabBuilder) {
                                setIsPopoverOpen(false);
                                navigate(
                                  `/dashboard/survey/${surveyId}/${CROSSTAB_BUILDER_TAB.id}${window.location.search}`
                                );
                                return;
                              }
                              handleExportDownload(exportStatXtabData);
                            }}
                          >
                            <span>Crosstabs & Stat Testing</span>
                          </button>
                        </li>
                      )}
                      {canPreferenceShare && (
                        <li>
                          <button onClick={() => handleExportDownload(exportMaxDiffPrefShareData)}>
                            <span>Preference Share</span>
                          </button>
                        </li>
                      )}
                      {survey && !isVideoSurvey && canExportReportQuestionnaire && (
                        <li>
                          <button onClick={() => handleExportDocx()}>
                            <span>Word Document</span>
                          </button>
                        </li>
                      )}
                      {!isVideoSurvey && (
                        <li>
                          <button onClick={() => handleExportPowerPoint()}>
                            <span>PowerPoint</span>
                          </button>
                        </li>
                      )}
                    </ul>
                  </OverlayPopover>,
                  hasTranslations ? (
                    <OverlayPopover
                      className={styles['dashboard-language-overlay']}
                      header="Available Languages"
                      isActive={isLanguagePopoverOpen}
                      key="language-popover"
                      onHide={() => setIsLanguagePopoverOpen(false)}
                      target={translateButtonRef?.current}
                    >
                      <div className={styles['dashboard-language-popover']}>
                        {availableLanguages.map(({ label, value }) => (
                          <Radio
                            isChecked={pendingLanguage === value}
                            key={value || 'default'}
                            label={label}
                            name="report-language"
                            onChange={() => setPendingLanguage(value)}
                            value={value}
                          />
                        ))}
                        <Button
                          className={styles['dashboard-language-popover-apply']}
                          onClick={handleApplyLanguage}
                          text="Apply"
                          variant="secondary"
                        />
                      </div>
                    </OverlayPopover>
                  ) : null,
                ].filter(Boolean)
          }
        >
          <h2>{surveyTitle}</h2>
        </Toolbar>
        {survey?.researchObjectives?.trim() && (
          <div className={styles['dashboard-research-objectives-collapsible']}>
            {showResearchObjective ? (
              <>
                <p className={styles['dashboard-research-objectives']}>
                  {survey.researchObjectives}
                </p>
                <ButtonText onClick={() => setShowResearchObjective(false)}>
                  <MinusIcon />
                  <span>
                    Hide <strong>Objective</strong>
                  </span>
                </ButtonText>
              </>
            ) : (
              <ButtonText onClick={() => setShowResearchObjective(true)}>
                <PlusIcon />
                <span>
                  Show <strong>Objective</strong>
                </span>
              </ButtonText>
            )}
          </div>
        )}
        <div>
          <p>
            {survey && survey.issueDatetime ? (
              <>
                Survey fielded on{' '}
                <strong>{getFormattedDate(new Date(survey.issueDatetime))}</strong>
              </>
            ) : (
              <>
                Survey created on{' '}
                <strong>{getFormattedDate(new Date(insightsDocument.created_datetime))}</strong>
              </>
            )}
          </p>
          {shouldShowLaunchPayment && (
            <p>
              Launched with <strong>{launchPayment}</strong>
            </p>
          )}
          {survey?.groups?.length > 1 && (
            <p>
              <strong>{survey.groups.length}</strong> quota groups
            </p>
          )}
          <p>
            <strong>{details?.completes || insightsDocument.completed}</strong>{' '}
            {survey?.groups?.length > 1 ? 'completed responses' : 'responses'}
          </p>
          <p>
            <strong>{questionsCount}</strong> {questionsCount === 1 ? 'question' : 'questions'}
          </p>
        </div>
        {showModalShare && (
          <ModalShare
            hasAllColleagues
            hasFindColleagues
            hasSharePublicly
            ids={[Number(surveyId)]}
            isSurvey
            setShowModalShare={setShowModalShare}
            shareItemWithClients={shareInstantSurveyWithClientGroup}
            shareItemWithUsers={shareInstantSurveyWithUsers}
          />
        )}

        {showModalSurveyPeopleGroup && (
          <ModalSurveyPeopleGroup
            compareBy={
              selectedCompareBy?.value !== compareByOptions[0].options[0].value
                ? selectedCompareBy?.value
                : ''
            }
            demographicFilter={selectedDemographicFilters}
            globalFilterFacets={globalFilterFacets}
            globalFilters={globalFilters}
            globalFilterSelection={globalFilterSelection}
            optionFilter={selectedOptionIds}
            query={exportQuery}
            quotaGroupFilter={selectedQuotaGroups}
            sampleSize={responseCounts?.filteredResponses || 0}
            setShowModalSurveyPeopleGroup={setShowModalSurveyPeopleGroup}
            showGlobalFiltersRedesign={showGlobalFiltersRedesign}
            surveyId={Number(surveyId)}
            surveyQuestions={surveyQuestions}
            surveyTitle={surveyTitle}
            totalResponses={responseCounts?.totalResponses || 0}
            useV2Endpoint={useV2SurveyResponses}
          />
        )}
        {insightsDocument.status !== surveyStatus.COMPLETED && insightsDocument.isPaused && (
          <Announcement
            className={styles['dashboard-announcement']}
            icon={<ColumnsIcon />}
            text="This survey is currently paused. New responses are not being collected at this time."
            variant="info"
          />
        )}
        <hr />
      </div>

      <Tabs
        routes={SURVEY_DASHBOARD_TABS.map(({ id, label }) => ({
          is_active: id === tabId,
          label,
          to: publicToken
            ? `/share/survey/${publicToken}/${surveyId}/${id}${window.location.search}`
            : `/dashboard/survey/${surveyId}/${id}${window.location.search}`,
        }))}
      />
      {tabId === 'details' && (
        <Layout.Column>
          {survey ? (
            <Details
              canShowFullQuestionnaire={canShowFullQuestionnaire}
              details={details}
              survey={translatedQuestionnaireSurvey}
              surveyQuestions={translatedSurveyQuestions}
              surveyType={surveyType}
            />
          ) : (
            <InsightsDetails
              details={details}
              survey={insightsDocument}
              surveyQuestions={translatedSurveyQuestions}
              surveyType={insightsType}
            />
          )}
        </Layout.Column>
      )}
      {tabId === 'responses' && (
        <Responses
          compareByOptions={compareByOptions}
          completionRateData={completionRateData}
          displayAs={displayAs}
          exportQuery={exportQuery}
          getQuestionSignificance={getQuestionSignificance}
          globalFilterFacets={globalFilterFacets}
          globalFilters={globalFilters}
          globalFilterSelection={globalFilterSelection}
          globalFiltersError={
            showGlobalFiltersRedesign ? globalFiltersRedesignError : globalFiltersError
          }
          handleUpdateIncludeDisqualifiedResponses={handleUpdateIncludeDisqualifiedResponses}
          includeDisqualifiedResponses={includeDisqualifiedResponses}
          isDataLoading={isResponseDataLoading}
          isFiltersLoading={
            showGlobalFiltersRedesign ? isGlobalFiltersRedesignLoading : isFiltersLoading
          }
          isGlobalFiltersRedesignEnabled={showGlobalFiltersRedesign}
          isVideoSurvey={isVideoSurvey}
          onDisplayAsChange={handleDisplayAsChange}
          onGlobalFilterSelectionChange={onGlobalFilterSelectionChange}
          onQuestionSignificanceChange={handleQuestionSignificanceChange}
          onQuestionSortByChange={handleQuestionSortByChange}
          onQuestionSortOrderChange={handleQuestionSortOrderChange}
          responseData={responseData}
          responseError={responseLoadingError}
          selectedCompareBy={selectedCompareBy}
          sortByGroupByQuestion={sortByGroupByQuestion}
          sortOrderByQuestion={sortOrderByQuestion}
          statusFilters={statusFilters}
          survey={survey}
          surveyId={surveyId}
          surveyQuestions={translatedSurveyQuestions}
          surveyResponseSummariesMap={keyFindingsMap}
          videoHighlights={videoHighlights}
        />
      )}
      {tabId === 'raw-data' && (
        <RawData
          isRawDataLoading={isRawDataLoading}
          isTooMuchData={
            (details?.completes || insightsDocument.completed) > details?.rawDataRowLimit
          }
          rawData={rawData}
          rawDataLoadingError={rawDataLoadingError}
        />
      )}
      {tabId === CROSSTAB_BUILDER_TAB.id && canShowCrosstabBuilder && (
        <CrosstabBuilder
          banner={crosstabBanner}
          globalFilters={globalFilters}
          globalFiltersError={globalFiltersError}
          isFiltersLoading={isFiltersLoading}
          isRawDataLoading={isRawDataLoading}
          questionConfigs={crosstabQuestionConfigs}
          rawData={rawData}
          setBanner={setCrosstabBanner}
          setQuestionConfigs={setCrosstabQuestionConfigs}
          surveyId={surveyId}
          surveyQuestions={surveyQuestions}
        />
      )}
      {tabId === TURF_SIMULATOR_TAB.id && canShowTurfSimulator && (
        <TurfSimulator
          globalFilters={globalFilters}
          globalFiltersError={globalFiltersError}
          isFiltersLoading={isFiltersLoading}
          isRawDataLoading={isTurfSimulatorDataLoading}
          onRetryRawData={getTurfSimulatorData}
          rawData={turfSimulatorData}
          rawDataLoadingError={turfSimulatorDataLoadingError}
          surveyQuestions={surveyQuestions}
        />
      )}
      {tabId === 'response-quality' && canShowResponseQuality && (
        <ResponseQuality
          includeDisqualified={
            statusFilters.canIncludeDisqualifiedResponses && includeDisqualifiedResponses
          }
          publicToken={publicToken}
          surveyId={survey.surveyRef}
          surveyQuestions={surveyQuestions}
        />
      )}
    </>
  );
};

Dashboard.propTypes = {
  details: PropTypes.object,
  id: PropTypes.number,
  insightsDocument: PropTypes.object.isRequired,
  insightsType: PropTypes.object,
  publicToken: PropTypes.string,
  survey: PropTypes.object,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
  surveyType: PropTypes.object,
};

export const DashboardSkeleton = () => (
  <LoaderSkeleton className={styles['dashboard-skeleton']} height={725}>
    <rect height="95" rx="4" ry="4" width="850" x="0" y="0" />
    <rect height="40" rx="4" ry="4" width="100%" x="0" y="105" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="155" />
    <rect height="165" rx="4" ry="4" width="100%" x="0" y="195" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="380" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="420" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="488" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="556" />
  </LoaderSkeleton>
);

const DashboardWithTabs = (props) => {
  return (
    <Routes>
      <Route element={<Dashboard {...props} />} path=":tab_id" />
      {/* cover case when no tab is selected */}
      <Route element={<Navigate replace to={DEFAULT_SURVEY_DASHBOARD_TAB_ID} />} path="*" />
    </Routes>
  );
};

export default Object.assign(DashboardWithTabs, {
  Skeleton: DashboardSkeleton,
});
